import { SchemesSearchDropdown } from "@transactions/components/schemes-search-dropdown";
import { ProposalFund, Scheme, SchemeType } from "creso-core/types";
import { X, PlusIcon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Button, CurrencyInput, cn } from "web-design-system";

const NO_SCHEME = 0;

function schemeStubFromFund<T extends SchemeType>(
  fund: ProposalFund
): Scheme<T> | null {
  const id = fund.fund_scheme;
  if (!id || id === NO_SCHEME) return null;
  return {
    id,
    scheme_name: fund.fund_scheme_name ?? "",
    scheme_isin: fund.fund_scheme_isin ?? "",
    scheme_code: "",
    scheme_type: "",
    amc_id: null,
    amc_name: null,
    amc_morningstar_code: fund.amc_code ?? null,
    fund_scheme_id: id,
  } as Scheme<T>;
}

type FundRowProps<T extends SchemeType> = {
  fund: ProposalFund;
  schemeType: T;
  /** Basket / proposal import rows lock scheme selection */
  lockScheme?: boolean;
  onRemove: () => void;
  onAmountChange: (amount: string) => void;
  onSchemeChange: (scheme: Scheme<T> | null) => void;
};

export function FundRow<T extends SchemeType>({
  fund,
  schemeType,
  lockScheme = false,
  onRemove,
  onAmountChange,
  onSchemeChange,
}: FundRowProps<T>) {
  const [localValue, setLocalValue] = useState(() => {
    let amount: number | undefined;
    if (fund.type === "LUMPSUM" && fund.lumpsum_amount) {
      amount = Number(fund.lumpsum_amount);
    } else if (fund.type === "SIP" && fund.sip_amount) {
      amount = Number(fund.sip_amount);
    }
    return Number.isNaN(amount!) ? "" : (amount?.toFixed(0) ?? "");
  });

  useEffect(() => {
    let amount: number | undefined;
    if (fund.type === "LUMPSUM" && fund.lumpsum_amount) {
      amount = Number(fund.lumpsum_amount);
    } else if (fund.type === "SIP" && fund.sip_amount) {
      amount = Number(fund.sip_amount);
    }
    setLocalValue(
      amount === undefined || Number.isNaN(amount) ? "" : amount.toFixed(0)
    );
  }, [fund.type, fund.lumpsum_amount, fund.sip_amount]);

  const schemeValue = useMemo(
    () => schemeStubFromFund<T>(fund),
    // Scheme dropdown value only depends on selected scheme fields, not fund_detail/remarks.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      fund.fund_scheme,
      fund.fund_scheme_name,
      fund.fund_scheme_isin,
      fund.amc_code,
    ]
  );

  const isFromFolio = !!fund.source_folio;
  const schemeDisabled = isFromFolio || lockScheme;

  return (
    <div className="items-center grid grid-cols-12 gap-3 rounded-lg py-1">
      <div className="col-span-8">
        <SchemesSearchDropdown
          type={schemeType}
          value={
            schemeValue
              ? {
                  label: schemeValue.scheme_name,
                  value: schemeValue.scheme_isin,
                }
              : null
          }
          onChange={onSchemeChange}
          placeholder="Select Scheme"
          disabled={schemeDisabled}
          align="start"
        />
      </div>
      <div className="col-span-3 shrink-0">
        <CurrencyInput
          placeholder="₹ Amount"
          value={localValue}
          decimalsLimit={0}
          onValueChange={(val) => {
            setLocalValue(val ?? "");
            if (!val) {
              onAmountChange("");
              return;
            }
            const n = Number(val);
            if (Number.isNaN(n)) return;
            onAmountChange(Math.max(0, n).toFixed(0));
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.currentTarget.blur();
            }
          }}
          disabled={isFromFolio}
        />
      </div>
      {!isFromFolio && (
        <Button
          onClick={onRemove}
          variant="ghost"
          size="icon"
          className="shrink-0 col-span-1"
          type="button"
          aria-label="Remove scheme"
        >
          <X className="size-4" />
        </Button>
      )}
    </div>
  );
}

export function AddNewSchemeButton({
  onClick,
  className,
}: {
  onClick: () => void;
  className?: string;
}) {
  return (
    <Button
      className={cn("w-fit", className)}
      variant="ghost"
      type="button"
      onClick={onClick}
    >
      <PlusIcon className="mr-1 size-4" />
      Add New Scheme
    </Button>
  );
}

export { NO_SCHEME as EMPTY_FUND_SCHEME_ID };
